import { ReactNode, useCallback, useMemo } from "react"
import {
   Paper,
   Table as MUITable,
   TableRow as MuiTableRow,
   TableCell as MuiTableCell,
   TableContainer,
} from "@mui/material"
import classNames from "classnames"
import { ReactSortable } from "react-sortablejs"
import { Header } from "./Header"
import { Row } from "./Row"
import { CellRendererArgs, Column } from "./types"
import { Icon, IconColor } from "@/components/shared/Icon"
import { useTableSort } from "@/hooks/useTableSort"
import "./index.scss"

export interface TableProps<T> {
   columns: Column<T>[]
   initialSortField?: keyof T | null
   rows: T[]
   editable?: boolean
   isSaveDisabled?: boolean
   editRowId?: string | null
   deleteRowId?: string | null
   validationErrors?: Record<string, boolean>
   rowsDraggable?: boolean
   rowLoading?: boolean
   error?: string
   extraActions?: ReactNode
   extraFields?: Partial<T>
   isAddingNewRow?: boolean
   className?: string
   fillHeight?: boolean
   isBulkCreating?: boolean
   bulkCreationIds?: string[]
   isRowDimmed?: (row: T) => boolean
   isRowEditDisabled?: (row: T) => boolean
   onRowsDrag?: (newOrders: T[]) => void
   onRowUpdate?: (updatedRow: T) => void
   onBulkItemDelete?: (id: string) => void
   onRowDelete?: (rowId: string) => void
   onEditClick?: (id: string) => void
   onInputChange?: (field: keyof T, value: string) => void
   onSave?: (form: T) => void
   onBulkSave?: () => void
   onCancel?: () => void
   onBulkCancel?: () => void
   onBulkCreate?: (rows: Partial<T>[]) => void
}

export function Table<T extends { id: string; draggable?: boolean }>({
   columns,
   initialSortField = null,
   rows,
   editable = true,
   isSaveDisabled = false,
   editRowId = null,
   deleteRowId = null,
   rowsDraggable = false,
   rowLoading = false,
   error,
   extraActions = null,
   extraFields = {},
   isAddingNewRow = false,
   className,
   fillHeight = false,
   isBulkCreating = false,
   bulkCreationIds = [],
   isRowDimmed = () => false,
   isRowEditDisabled = () => false,
   onRowsDrag = () => {},
   validationErrors = {},
   onBulkItemDelete = () => {},
   onRowDelete = () => {},
   onEditClick = () => {},
   onInputChange = () => {},
   onBulkSave = () => {},
   onSave = () => {},
   onCancel = () => {},
   onBulkCancel = () => {},
   onBulkCreate,
}: TableProps<T>) {
   const {
      data: tableRows,
      sortField,
      sortDirection,
      handleSort,
   } = useTableSort<T>(rows, initialSortField)

   const isTableEmpty = useMemo(() => {
      return tableRows.length === 0
   }, [tableRows])

   const isActiveRow = useCallback(
      (id: string) => {
         return editRowId === id || deleteRowId === id
      },
      [editRowId, deleteRowId]
   )

   const handlePasted = (rows: string[], column: Column<T>) => {
      if (!onBulkCreate) {
         return
      }
      const newRowsData = rows.map((row) => {
         const values = row.split("\t")
         const newRow: Partial<T> = {}
         const columnIndex = columns.findIndex((c) => c.key === column.key)

         columns.forEach((col, index) => {
            if (index >= columnIndex && values[index - columnIndex]) {
               newRow[col.key] = values[index - columnIndex] as T[keyof T]
            }
         })
         return newRow
      })

      if (newRowsData.length > 1) {
         onBulkCreate(newRowsData)
      }
   }

   const handleEdit = (id: string) => {
      onEditClick(id)
   }

   const handleInputChange = (field: keyof T, value: string) => {
      onInputChange(field, value)
   }

   const handleBulkSave = () => {
      onBulkSave()
   }

   const handleSave = (form: T) => {
      onSave(form)
   }

   const handleCancel = () => {
      onCancel()
   }

   const handleBulkCancel = () => {
      onBulkCancel()
   }

   const handleDelete = (id: string) => {
      onRowDelete(id)
   }

   const handleBulkItemDelete = (id: string) => {
      onBulkItemDelete(id)
   }

   const displayColumns: Column<T>[] = rowsDraggable
      ? [
           {
              key: "drag-column" as keyof T,
              label: "",
              sortable: false,
              dimmable: false,
              renderCell: (props: CellRendererArgs<T>) => {
                 const { row } = props
                 if (!row.draggable) {
                    return
                 }
                 return (
                    <Icon
                       name="gripDots"
                       color={IconColor.COOL_GRAY}
                    />
                 )
              },
           } as unknown as Column<T>,
           ...columns,
        ]
      : columns

   return (
      <Paper
         elevation={0}
         className={classNames("table__container", {
            "table__container--fill": fillHeight,
         })}
      >
         <TableContainer
            className={classNames(
               "table__container",
               { "table__container--fill": fillHeight },
               className
            )}
         >
            <MUITable
               stickyHeader
               className="table"
            >
               <Header<T>
                  columns={displayColumns}
                  onSort={handleSort}
                  sortField={sortField}
                  sortDirection={sortDirection}
                  editable={editable}
               />
               {isTableEmpty ? (
                  <tbody>
                     <MuiTableRow className="table__row">
                        <MuiTableCell
                           className="table__cell"
                           colSpan={
                              editable
                                 ? displayColumns.length + 1
                                 : displayColumns.length
                           }
                        >
                           <div
                              className={classNames("table__cell-message", {
                                 "table__cell--error": !!error,
                              })}
                           >
                              {error ? error : "This table has no entries yet"}
                           </div>
                        </MuiTableCell>
                     </MuiTableRow>
                  </tbody>
               ) : (
                  <ReactSortable
                     sort={rowsDraggable}
                     tag="tbody"
                     list={tableRows.map((row) => ({ ...row }))}
                     setList={onRowsDrag}
                     animation={150}
                     handle=".table__cell--draggable"
                     filter=".table__row--not-draggable"
                     preventOnFilter={true}
                     onMove={(e) =>
                        !e.related.classList.contains(
                           "table__row--not-draggable"
                        )
                     }
                  >
                     {tableRows.map((row) => (
                        <Row<T>
                           key={row.id}
                           row={row}
                           {...(extraFields &&
                              isActiveRow(row.id) && { extraFields })}
                           loading={
                              rowLoading &&
                              (isActiveRow(row.id) ||
                                 bulkCreationIds.includes(row.id))
                           }
                           error={
                              (error && isActiveRow(row.id)) ||
                              (isBulkCreating &&
                                 error &&
                                 row.id === tableRows[tableRows.length - 1].id)
                                 ? error
                                 : null
                           }
                           isNewRow={isAddingNewRow && isActiveRow(row.id)}
                           draggable={row.draggable}
                           columns={displayColumns}
                           isDisabled={
                              (editRowId !== null && editRowId !== row.id) ||
                              (isBulkCreating &&
                                 !bulkCreationIds.includes(row.id))
                           }
                           isDimmed={isRowDimmed(row)}
                           isSaveDisabled={isSaveDisabled}
                           isEditDisabled={isRowEditDisabled(row)}
                           isEditing={
                              editable &&
                              (row.id === editRowId ||
                                 (isBulkCreating &&
                                    bulkCreationIds.includes(row.id)))
                           }
                           validationErrors={validationErrors}
                           onEdit={handleEdit}
                           onBulkSave={handleBulkSave}
                           onSave={handleSave}
                           onCancel={handleCancel}
                           onBulkCancel={handleBulkCancel}
                           onDelete={handleDelete}
                           onBulkItemDelete={handleBulkItemDelete}
                           onInputChange={handleInputChange}
                           onPasted={handlePasted}
                           editable={editable}
                           isFirstEditableRow={
                              isBulkCreating && bulkCreationIds[0] === row.id
                           }
                           isBulkCreating={
                              isBulkCreating && bulkCreationIds.includes(row.id)
                           }
                           extraActions={extraActions}
                        />
                     ))}
                  </ReactSortable>
               )}
            </MUITable>
         </TableContainer>
      </Paper>
   )
}
